import { Component, ErrorInfo, ReactNode } from 'react';

type RemoteErrorBoundaryProps = {
  children: ReactNode;
  /** Renders the error view; `reset` clears the error and renders the children again. */
  fallback: (props: { error: Error; reset: () => void }) => ReactNode;
  onError: (error: Error, info: ErrorInfo) => void;
  onReset?: () => void;
  /** The boundary resets by itself when this value changes while it shows the error (e.g. the pathname). */
  resetKey?: string;
};

type RemoteErrorBoundaryState = {
  error: Error | null;
};

/**
 * Catches errors thrown while a remote renders, including its internal chunks failing to load
 * after mount, so they never unmount the whole shell.
 *
 * A class on purpose: React only supports error boundaries as class components, which makes this
 * the one exception to the arrow-functions rule.
 */
export class RemoteErrorBoundary extends Component<RemoteErrorBoundaryProps, RemoteErrorBoundaryState> {
  state: RemoteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RemoteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const { onError } = this.props;
    onError(error, info);
  }

  componentDidUpdate(prevProps: RemoteErrorBoundaryProps): void {
    const { resetKey } = this.props;
    const { error } = this.state;
    if (error && prevProps.resetKey !== resetKey) {
      this.reset();
    }
  }

  reset = (): void => {
    const { onReset } = this.props;
    onReset?.();
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { children, fallback } = this.props;
    const { error } = this.state;
    if (error) {
      return fallback({ error, reset: this.reset });
    }
    return children;
  }
}
